import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { RotateCcw, TrendingUp, TrendingDown } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const INCOME_FIELDS = [
  { key: "salary", label: "Lønn / inntekt", testid: "income-input-salary" },
  { key: "benefits", label: "Trygd / stønader", testid: "income-input-benefits" },
  { key: "childBenefit", label: "Barnetrygd", testid: "income-input-child-benefit" },
  { key: "housingSupport", label: "Bostøtte", testid: "income-input-housing-support" },
];

const EXPENSE_FIELDS = [
  { key: "housing", label: "Husleie / boliglån", testid: "expense-input-housing", color: "#2C4C43" },
  { key: "energy", label: "Strøm", testid: "expense-input-energy", color: "#5E8C7E" },
  { key: "food", label: "Mat & drikke", testid: "expense-input-food", color: "#C86D51" },
  { key: "transport", label: "Transport", testid: "expense-input-transport", color: "#8A9B8E" },
  { key: "loans", label: "Lån & renter", testid: "expense-input-loans", color: "#D9A441" },
  { key: "other", label: "Annet", testid: "expense-input-other", color: "#7A8CA3" },
];

const ZERO = Object.fromEntries([...INCOME_FIELDS, ...EXPENSE_FIELDS].map((f) => [f.key, ""]));
const fmt = new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 0 });
const num = (v) => Number(v) || 0;

export default function BudgetTool() {
  const [values, setValues] = useState(ZERO);

  const totalIncome = useMemo(() => INCOME_FIELDS.reduce((s, f) => s + num(values[f.key]), 0), [values]);
  const totalExpense = useMemo(() => EXPENSE_FIELDS.reduce((s, f) => s + num(values[f.key]), 0), [values]);
  const balance = totalIncome - totalExpense;

  const chartData = useMemo(
    () => EXPENSE_FIELDS.filter((f) => num(values[f.key]) > 0).map((f) => ({ name: f.label, value: num(values[f.key]), color: f.color })),
    [values]
  );

  const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value.replace(/[^0-9]/g, "") }));

  const renderField = (f) => (
    <label key={f.key} className="block">
      <span className="text-xs font-semibold text-muted-foreground">{f.label}</span>
      <div className="mt-1.5 relative">
        <input
          data-testid={f.testid}
          inputMode="numeric"
          value={values[f.key]}
          onChange={set(f.key)}
          placeholder="0"
          className="w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm font-mono outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all duration-200"
        />
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-mono text-muted-foreground">kr</span>
      </div>
    </label>
  );

  return (
    <section id="budsjett" data-testid="budget-calculator-section" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-mono font-semibold text-terracotta">Verktøy 01</p>
          <h2 className="mt-3 font-serif font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-primary">
            Budsjettkalkulator
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
            Fyll inn tallene dine — ingenting lagres eller sendes noe sted. Alt skjer i nettleseren din.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 bg-surface border border-border rounded-3xl p-6 lg:p-8">
            <h3 className="font-serif font-bold text-xl">Inntekter per måned</h3>
            <div className="mt-5 space-y-4">{INCOME_FIELDS.map(renderField)}</div>
          </div>

          <div className="lg:col-span-4 bg-surface border border-border rounded-3xl p-6 lg:p-8">
            <h3 className="font-serif font-bold text-xl">Utgifter per måned</h3>
            <div className="mt-5 space-y-4">{EXPENSE_FIELDS.map(renderField)}</div>
          </div>

          <div className="lg:col-span-4 flex flex-col gap-6">
            <div className="bg-surface border border-border rounded-3xl p-6 lg:p-8">
              <h3 className="font-serif font-bold text-xl">Oppsummering</h3>
              <dl className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between"><dt className="text-muted-foreground">Inn</dt><dd className="font-mono font-semibold">{fmt.format(totalIncome)} kr</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Ut</dt><dd className="font-mono font-semibold">{fmt.format(totalExpense)} kr</dd></div>
              </dl>
              <div
                data-testid="budget-balance-badge"
                className={`mt-6 rounded-2xl px-5 py-4 flex items-center gap-3 ${
                  balance >= 0 ? "bg-accent text-accent-foreground" : "bg-terracotta/10 text-terracotta"
                }`}
              >
                {balance >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                <div>
                  <p className="text-xs font-mono uppercase tracking-[0.15em]">{balance >= 0 ? "Overskudd" : "Underskudd"}</p>
                  <p className="font-serif font-bold text-2xl">{fmt.format(Math.abs(balance))} kr</p>
                </div>
              </div>
              <button
                data-testid="budget-reset-btn"
                onClick={() => setValues(ZERO)}
                className="mt-6 inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
              >
                <RotateCcw size={13} /> Nullstill skjema
              </button>
            </div>

            <div data-testid="budget-recharts-container" className="bg-surface border border-border rounded-3xl p-6 lg:p-8 flex-1 min-h-[240px]">
              <h3 className="font-serif font-bold text-xl">Fordeling av utgifter</h3>
              {chartData.length ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={chartData} dataKey="value" innerRadius={52} outerRadius={80} paddingAngle={3} strokeWidth={0}>
                      {chartData.map((d) => <Cell key={d.name} fill={d.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${fmt.format(v)} kr`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="mt-8 text-sm text-muted-foreground">Fyll inn utgifter for å se fordelingen.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
